import { useEffect, useRef } from 'react'
import './horizontal-drag.scss'

export const PROPERTY_DRAG_RAILS = '.property-ad-banner-track, .property-featured-listing-row, .property-agent-scroll-row, .property-info-article-row, .property-detail-recommendation-row, .property-detail-type-grid, .property-detail-gallery-strip, .property-detail-thumb-grid'

export function useHorizontalDrag(selector: string, onSwipe?: (direction: number) => void) {
  const callback = useRef(onSwipe)
  useEffect(() => { callback.current = onSwipe }, [onSwipe])

  useEffect(() => {
    let gesture: { element: HTMLElement; id: number; x: number; y: number; left: number; dragging: boolean } | null = null
    let blocked: HTMLElement | null = null
    let blockedUntil = 0

    const finish = () => {
      if (!gesture) return
      const { element, id } = gesture
      gesture = null
      element.classList.remove('is-pointer-dragging')
      if (element.hasPointerCapture(id)) element.releasePointerCapture(id)
    }
    const down = (event: PointerEvent) => {
      if (!event.isPrimary || event.button !== 0 || !(event.target instanceof Element)) return
      finish()
      blocked = null
      const element = event.target.closest<HTMLElement>(selector)
      if (!element || event.target.closest('input, textarea, select, [contenteditable="true"]')) return
      // Touch rails retain native momentum scrolling; only the slide switcher needs custom swipe.
      if (!callback.current && (event.pointerType !== 'mouse' || element.scrollWidth <= element.clientWidth)) return
      gesture = { element, id: event.pointerId, x: event.clientX, y: event.clientY, left: element.scrollLeft, dragging: false }
    }
    const move = (event: PointerEvent) => {
      if (!gesture || event.pointerId !== gesture.id) return
      const dx = event.clientX - gesture.x
      const dy = event.clientY - gesture.y
      if (!gesture.dragging) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) { finish(); return }
        if (Math.abs(dx) < 8) return
        gesture.dragging = true
        gesture.element.classList.add('is-pointer-dragging')
        gesture.element.setPointerCapture(event.pointerId)
      }
      event.preventDefault()
      if (!callback.current) gesture.element.scrollLeft = gesture.left - dx
    }
    const up = (event: PointerEvent) => {
      if (!gesture || event.pointerId !== gesture.id) return
      if (gesture.dragging) {
        blocked = gesture.element
        blockedUntil = Date.now() + 500
        const distance = event.clientX - gesture.x
        if (Math.abs(distance) >= 40) callback.current?.(distance < 0 ? 1 : -1)
      }
      finish()
    }
    const click = (event: MouseEvent) => {
      if (event.detail > 0 && blocked && Date.now() < blockedUntil && event.target instanceof Node && blocked.contains(event.target)) {
        event.preventDefault()
        event.stopImmediatePropagation()
      }
    }
    const nativeDrag = (event: DragEvent) => {
      if (gesture) event.preventDefault()
    }
    const lostCapture = (event: PointerEvent) => {
      if (gesture && event.target === gesture.element && !gesture.element.hasPointerCapture(gesture.id)) finish()
    }
    document.addEventListener('pointerdown', down)
    document.addEventListener('pointermove', move, { passive: false })
    document.addEventListener('pointerup', up)
    document.addEventListener('pointercancel', finish)
    document.addEventListener('lostpointercapture', lostCapture)
    document.addEventListener('click', click, true)
    document.addEventListener('dragstart', nativeDrag)
    window.addEventListener('blur', finish)
    return () => {
      finish()
      document.removeEventListener('pointerdown', down)
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', up)
      document.removeEventListener('pointercancel', finish)
      document.removeEventListener('lostpointercapture', lostCapture)
      document.removeEventListener('click', click, true)
      document.removeEventListener('dragstart', nativeDrag)
      window.removeEventListener('blur', finish)
    }
  }, [selector])
}
